import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Select, Switch, Checkbox } from "antd";
import { Leaf, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { ContainerCard } from "../ui/cards";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../common/ToastMsg";
import { NURTURE_URL, NURTURE_LOG_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { TEXT, joinSpec, splitSpec, templatePositions } from "./LeadWelcomeWhatsAppCard";

const STATUS_STYLE = {
    sending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
    sent:    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    skipped: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
    failed:  'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};
const STATUS_LABEL = { sending: 'Sending', sent: 'Sent', skipped: 'Not sent', failed: 'Failed' };
const LABEL = "text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block";

const fmt = (d) => (d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '');
export const dayLabel = (n) => (n === 0 ? 'Same day' : n % 30 === 0 && n >= 30 ? `Day ${n} (${n / 30} mo)` : `Day ${n}`);

const formOf = ({ mailboxes, email_templates, wa_accounts, wa_templates, wa_fields, ...rest }) =>
    ({ ...rest, steps: (rest.steps || []).map(s => ({ ...s, wa_variables: s.wa_variables || {} })) });

/**
 * Settings → Email Templates: the nurture sequence for leads parked in the
 * Nurture stage (a real buyer who does not want it now). Each step is a day
 * after the move and an email template and/or an approved WhatsApp template.
 * A step goes out on its day at the send time; only the latest due step is
 * sent, so a lead never gets a burst. Every email carries an unsubscribe link.
 * Backend: email/nurture/ (platform_core/email_integration/nurture.py).
 * Off until switched on.
 */
export default function NurtureCard({ templates, onCreateTemplate }) {
    const { fetch, update } = useApiMethods();
    const navigate = useNavigate();
    const [form, setForm] = useState(null);
    const [options, setOptions] = useState({ mailboxes: [], email_templates: [], wa_accounts: [], wa_templates: [], wa_fields: [] });
    const [log, setLog] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const take = (data) => {
        setForm(formOf(data));
        setOptions({ mailboxes: data.mailboxes || [], email_templates: data.email_templates || [],
                     wa_accounts: data.wa_accounts || [], wa_templates: data.wa_templates || [],
                     wa_fields: data.wa_fields || [] });
    };

    useEffect(() => {
        fetch(NURTURE_URL).then(cfg => {
            if (cfg?.status === 'success') { take(cfg.data); setError(''); }
            else setError(cfg?.message || 'Could not load the lead nurture settings');
        });
        fetch(NURTURE_LOG_URL).then(res => { if (res?.status === 'success') setLog(res.data || []); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fieldKeys = options.wa_fields.map(f => f.key);
    const waTemplateOf = (id) => options.wa_templates.find(t => t.id === id);

    const save = async (patch = {}) => {
        const steps = form.steps.map(s => {
            const positions = templatePositions(waTemplateOf(s.wa_template_id));
            return { ...s, wa_variables: Object.fromEntries(positions.map(p => [p, s.wa_variables[p] || ''])) };
        });
        setSaving(true);
        const res = await update(NURTURE_URL, { ...form, steps, ...patch });
        setSaving(false);
        if (res?.status === 'success') {
            take(res.data);
            const which = 'wa_enabled' in patch ? 'WhatsApp' : 'Email';
            const on = patch.is_enabled ?? patch.wa_enabled;
            ToastMsg('success', on === undefined ? 'Saved' : `${which} nurture switched ${on ? 'on' : 'off'}`);
        } else {
            ToastMsg('error', res?.message || 'Save failed');
        }
    };

    if (error) {
        return <ContainerCard><p className="text-sm text-red-600 dark:text-red-400">{error}</p></ContainerCard>;
    }
    if (!form) {
        return (
            <ContainerCard>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading lead nurture…
                </div>
            </ContainerCard>
        );
    }

    const set = (key) => (v) => setForm(p => ({ ...p, [key]: v ?? null }));
    const setStep = (i, patch) => setForm(p => ({ ...p, steps: p.steps.map((s, j) => (j === i ? { ...s, ...patch } : s)) }));
    const setVar = (i, pos, part) => setForm(p => ({
        ...p, steps: p.steps.map((s, j) => {
            if (j !== i) return s;
            const cur = splitSpec(s.wa_variables[pos], fieldKeys);
            return { ...s, wa_variables: { ...s.wa_variables, [pos]: joinSpec({ ...cur, ...part }) } };
        }),
    }));
    const addStep = () => setForm(p => {
        const last = p.steps.reduce((m, s) => Math.max(m, Number(s.day_offset) || 0), 0);
        return { ...p, steps: [...p.steps, { id: null, day_offset: last ? last + 30 : 7, email_template_id: null,
                                             wa_template_id: null, wa_variables: {}, is_active: true }] };
    });
    const removeStep = (i) => setForm(p => ({ ...p, steps: p.steps.filter((_, j) => j !== i) }));
    const accountTemplates = options.wa_templates.filter(
        t => !form.wa_account_id || !t.account_id || t.account_id === form.wa_account_id);
    const fieldOptions = [
        ...options.wa_fields.map(f => ({ value: f.key, label: f.label })),
        { value: TEXT, label: 'Fixed text…' },
    ];

    return (
        <ContainerCard>
            <div className="flex items-start gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg bg-brand-50 dark:bg-brand-900/30 flex items-center justify-center shrink-0">
                    <Leaf className="h-4 w-4 text-brand-600 dark:text-brand-300" />
                </div>
                <div>
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Lead nurture</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                        For a real lead who doesn't want it now: move them to <b>Nurture</b> (not Dis-Qualified) with a reason and
                        a revisit date. They get the steps below on their day, until someone moves them out of Nurture Lead.
                        The owner is told on the revisit date, and whenever the lead clicks a link, replies on WhatsApp or
                        enquires again. Every email has an unsubscribe link; WhatsApp "STOP" is honoured.
                    </p>
                </div>
            </div>

            <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Steps</h3>
                    <div className="flex items-center gap-3">
                        <label className="text-xs text-gray-600 dark:text-gray-400" htmlFor="nurture-send-time">Send at</label>
                        <Input id="nurture-send-time" type="time" className="w-28" value={form.send_time}
                               onChange={(e) => set('send_time')(e.target.value)} />
                    </div>
                </div>
                {form.steps.length === 0 && (
                    <p className="text-xs text-gray-400 mb-3">No steps yet. Add one, e.g. day 7, 30, 60, 90.</p>
                )}
                <div className="space-y-3">
                    {form.steps.map((s, i) => {
                        const wt = waTemplateOf(s.wa_template_id);
                        const positions = templatePositions(wt);
                        return (
                            <div key={s.id || `new-${i}`}
                                 className={cn("rounded-lg border p-3", s.is_active
                                     ? "border-gray-200 dark:border-gray-700"
                                     : "border-dashed border-gray-200 dark:border-gray-700 opacity-70")}>
                                <div className="grid grid-cols-1 md:grid-cols-[110px_1fr_1fr_auto] gap-3 items-end">
                                    <div>
                                        <label className={LABEL} htmlFor={`nurture-day-${i}`}>Day after move</label>
                                        <Input id={`nurture-day-${i}`} type="number" min={0} max={730} value={s.day_offset}
                                               onChange={(e) => setStep(i, { day_offset: Number(e.target.value) })} />
                                    </div>
                                    <div>
                                        <label className={LABEL}>Email template</label>
                                        <Select className="w-full" placeholder="No email" allowClear showSearch
                                                optionFilterProp="label" value={s.email_template_id ?? undefined}
                                                aria-label={`Step ${i + 1} email template`}
                                                options={(templates || options.email_templates).map(t => ({ value: t.id, label: t.name }))}
                                                onChange={(v) => setStep(i, { email_template_id: v ?? null })} />
                                    </div>
                                    <div>
                                        <label className={LABEL}>WhatsApp template</label>
                                        <Select className="w-full" placeholder="No WhatsApp" allowClear showSearch
                                                optionFilterProp="label" value={s.wa_template_id ?? undefined}
                                                aria-label={`Step ${i + 1} WhatsApp template`}
                                                options={accountTemplates.map(t => ({ value: t.id, label: `${t.name} · ${t.category}` }))}
                                                notFoundContent={<span className="text-xs text-gray-500">No approved templates - sync them under Settings → WA Templates</span>}
                                                onChange={(v) => setStep(i, { wa_template_id: v ?? null, wa_variables: {} })} />
                                    </div>
                                    <div className="flex items-center gap-2 pb-1">
                                        <Switch size="small" checked={s.is_active} aria-label={`Step ${i + 1} on`}
                                                onChange={(v) => setStep(i, { is_active: v })} />
                                        <button type="button" aria-label={`Remove step ${i + 1}`} onClick={() => removeStep(i)}
                                                className="p-1 text-gray-400 hover:text-red-600">
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                                {wt && positions.length > 0 && (
                                    <div className="mt-3 space-y-2">
                                        <p className="text-xs text-gray-700 dark:text-gray-200 whitespace-pre-wrap rounded-lg bg-green-50/60 dark:bg-green-900/10 border border-green-100 dark:border-green-900/40 p-2">
                                            {wt.body_text}
                                        </p>
                                        {positions.map(pos => {
                                            const v = splitSpec(s.wa_variables[pos], fieldKeys);
                                            return (
                                                <div key={pos} className="flex items-center gap-2">
                                                    <span className="w-10 text-xs font-mono text-gray-500">{`{{${pos}}}`}</span>
                                                    <Select className="flex-1 min-w-0" placeholder="Fill with…" value={v.field || undefined}
                                                            options={fieldOptions} aria-label={`Step ${i + 1} variable ${pos}`}
                                                            onChange={(f) => setVar(i, pos, { field: f, text: f === TEXT ? v.text : '' })} />
                                                    {v.field === TEXT ? (
                                                        <Input className="flex-1" value={v.text} placeholder="Text to send"
                                                               onChange={(e) => setVar(i, pos, { text: e.target.value })} />
                                                    ) : v.field && (
                                                        <Input className="flex-1" value={v.fallback} placeholder="If blank, send…"
                                                               onChange={(e) => setVar(i, pos, { fallback: e.target.value })} />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                <div className="flex items-center gap-4 mt-3">
                    <button type="button" onClick={addStep}
                            className="flex items-center gap-1 text-xs font-medium text-brand-600 dark:text-brand-300 hover:underline">
                        <Plus className="h-3 w-3" /> Add step
                    </button>
                    {onCreateTemplate && (
                        <button type="button" onClick={onCreateTemplate}
                                className="flex items-center gap-1 text-xs font-medium text-brand-600 dark:text-brand-300 hover:underline">
                            <Plus className="h-3 w-3" /> New email template
                        </button>
                    )}
                </div>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2">
                    Email templates can use {'{{first_name}}'}, {'{{company_name}}'}, {'{{sales_rep_name}}'} and
                    {' {{unsubscribe_link}}'} (added at the end when missing). A step added after its day has passed for a
                    lead is skipped for that lead.
                </p>
            </section>

            <div className="mt-5 grid grid-cols-1 xl:grid-cols-2 gap-4">
                <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Email</h3>
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600 dark:text-gray-300">{form.is_enabled ? 'On' : 'Off'}</span>
                            <Switch checked={form.is_enabled} loading={saving} aria-label="Email nurture"
                                    onChange={(v) => save({ is_enabled: v })} />
                        </div>
                    </div>
                    <label className={LABEL}>Send from</label>
                    <Select className="w-full" placeholder="Choose a mailbox" allowClear
                            value={form.sender_account_id ?? undefined}
                            options={options.mailboxes.map(m => ({
                                value: m.id, disabled: !m.can_send,
                                label: `${m.email_address}${m.owner ? ` (${m.owner})` : ''}${m.can_send ? '' : ' - SMTP not verified'}`,
                            }))}
                            onChange={set('sender_account_id')} />
                    <Checkbox className="mt-2" checked={form.send_from_owner}
                              onChange={(e) => set('send_from_owner')(e.target.checked)}>
                        <span className="text-xs text-gray-600 dark:text-gray-300">
                            Use the lead owner's own mailbox when they have one
                        </span>
                    </Checkbox>
                </section>

                <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">WhatsApp</h3>
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600 dark:text-gray-300">{form.wa_enabled ? 'On' : 'Off'}</span>
                            <Switch checked={form.wa_enabled} loading={saving} aria-label="WhatsApp nurture"
                                    onChange={(v) => save({ wa_enabled: v })} />
                        </div>
                    </div>
                    <label className={LABEL}>Send from</label>
                    <Select className="w-full" placeholder="WhatsApp account" allowClear
                            value={form.wa_account_id ?? undefined}
                            options={options.wa_accounts.map(a => ({
                                value: a.id, label: `${a.name}${a.phone_number ? ` (${a.phone_number})` : ''}`,
                            }))}
                            onChange={set('wa_account_id')} />
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2">
                        Nurture messages are promotional, so WhatsApp needs approved Marketing templates.
                    </p>
                </section>
            </div>

            <div className="flex justify-end mt-4">
                <Button onClick={() => save()} disabled={saving} className="text-sm flex items-center gap-2">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                </Button>
            </div>

            <div className="mt-6">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Recent nurture messages</h3>
                {log.length === 0 ? (
                    <p className="text-xs text-gray-400">No nurture messages yet.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left border-b border-gray-200 dark:border-gray-700">
                                    {['Lead', 'Step', 'Channel', 'Status', 'To', 'When'].map(h => (
                                        <th key={h} className="pb-2 pr-4 text-xs font-semibold text-gray-500 dark:text-gray-400">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                {log.map(r => (
                                    <tr key={r.id}>
                                        <td className="py-2 pr-4">
                                            <button type="button" className="text-brand-600 dark:text-brand-300 hover:underline text-left"
                                                    onClick={() => navigate(`/customers/customerdetails?id=${r.customer_id}`,
                                                        { state: { formEdit: true, row: { id: r.customer_id } } })}>
                                                {r.customer_name || `Lead #${r.customer_id}`}
                                            </button>
                                        </td>
                                        <td className="py-2 pr-4 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">
                                            {r.day_offset == null ? '—' : dayLabel(r.day_offset)}
                                        </td>
                                        <td className="py-2 pr-4 text-xs text-gray-600 dark:text-gray-300">{r.channel === 'whatsapp' ? 'WhatsApp' : 'Email'}</td>
                                        <td className="py-2 pr-4">
                                            <span className={cn("px-2 py-0.5 text-[10px] font-medium rounded-full", STATUS_STYLE[r.status])}>
                                                {STATUS_LABEL[r.status] || r.status}
                                            </span>
                                            {r.reason && <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{r.reason}</span>}
                                        </td>
                                        <td className="py-2 pr-4 text-xs text-gray-600 dark:text-gray-300">{r.sent_to || '—'}</td>
                                        <td className="py-2 text-xs text-gray-500 dark:text-gray-400">{fmt(r.sent_at || r.due_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </ContainerCard>
    );
}
